import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Crown, Sparkles, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";

interface Plan {
  plan_name: string;
  plan_type: string;
  credits_amount: number;
  price: number;
  currency_symbol: string;
  features: string[];
}

interface PlanComparisonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlanType?: string | null;
}

export const PlanComparisonModal = ({ open, onOpenChange, currentPlanType }: PlanComparisonModalProps) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-plans');
        
        if (error) throw error;

        if (data?.plans) {
          setPlans(data.plans);
        }
      } catch (err) {
        console.error('Error fetching plans:', err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchPlans();
    }
  }, [open]);

  const getPlanIcon = (planType: string) => {
    switch(planType.toLowerCase()) {
      case 'starter':
        return Zap;
      case 'core':
        return Crown;
      case 'pro_ai':
        return Sparkles;
      default:
        return Crown;
    }
  };

  const getPlanColor = (planType: string) => {
    switch(planType.toLowerCase()) {
      case 'starter':
        return 'border-blue-500/50 bg-blue-500/5';
      case 'core':
        return 'border-primary/50 bg-primary/5';
      case 'pro_ai':
        return 'border-accent/50 bg-accent/5';
      default:
        return 'border-border';
    }
  };

  const isCurrentPlan = (planType: string) => {
    return currentPlanType?.toLowerCase() === planType.toLowerCase();
  };

  const handleSelectPlan = (planType: string) => {
    onOpenChange(false);
    navigate('/credits');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">Choose Your Plan</DialogTitle>
          <p className="text-muted-foreground text-center">Select the plan that fits your needs</p>
        </DialogHeader>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6">
                <Skeleton className="h-8 w-24 mb-4" />
                <Skeleton className="h-12 w-full mb-2" />
                <Skeleton className="h-4 w-32 mb-6" />
                <Skeleton className="h-10 w-full mb-4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {plans.map((plan) => {
              const Icon = getPlanIcon(plan.plan_type);
              const isCurrent = isCurrentPlan(plan.plan_type);
              const colorClass = getPlanColor(plan.plan_type);
              
              return (
                <Card 
                  key={plan.plan_type}
                  className={`p-6 relative transition-all hover:shadow-lg ${colorClass} ${
                    isCurrent ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                      Current Plan
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-bold">{plan.plan_name}</h3>
                  </div>

                  <div className="mb-2">
                    <span className="text-4xl font-bold">{plan.currency_symbol}{plan.price}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-6">
                    {plan.credits_amount} AI credits/month
                  </p>

                  <Button 
                    onClick={() => handleSelectPlan(plan.plan_type)}
                    disabled={isCurrent}
                    className="w-full mb-6"
                    variant={isCurrent ? "outline" : "default"}
                  >
                    {isCurrent ? 'Current Plan' : 'Select Plan'}
                  </Button>

                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-foreground">Features:</p>
                    {plan.features && plan.features.length > 0 ? (
                      plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">
                          {plan.credits_amount} AI credits per month
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>All plans include access to core features. Upgrade anytime.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
